import './config/i18n';
import React, { useState, useEffect, lazy, Suspense } from 'react';
import settings from './config/settings';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';

// Navigation eager laden: liegt above-the-fold, enthält alle Nav-Anker und wird auf
// jeder Route sofort gebraucht. lazy() ließe die Anker asynchron mounten — genau im
// Zeitfenster, in dem Lighthouses AnchorElements-Gatherer die DOM-Pfade auflöst; ein
// spät gemounteter/getauschter Anker macht den Pfad stale und crasht den Gatherer
// ("Cannot read properties of undefined (reading 'objectId')"), was die SEO-Kategorie rot färbt.
import NavBarDesktop from './components/NavBarDesktop';
import NavBarMobile from './components/NavBarMobile';
import ScrollToTopArrow from './components/ScrollToTopArrow';

// Dynamisch importierte Komponenten
const Why = lazy(() => import('./components/Why'));
const Statement = lazy(() => import('./components/Statement'));
const Problem = lazy(() => import('./components/Problem'));
const Mission = lazy(() => import('./components/Mission'));
const References = lazy(() => import('./components/References'));
const How = lazy(() => import('./components/How'));
const Library = lazy(() => import('./components/Library'));
const Tools = lazy(() => import('./components/Tools'));
const Who = lazy(() => import('./components/Who'));
const WriteUs = lazy(() => import('./components/WriteUs'));
const Footer = lazy(() => import('./components/Footer'));
const Imprint = lazy(() => import('./components/Imprint'));
const Privacy = lazy(() => import('./components/Privacy'));
const NotFound = lazy(() => import('./components/NotFound'));
const ScrollToAnchor = lazy(() => import('./components/ScrollToAnchor'));

const CANONICAL_BASE = 'https://scalamento.de';

function Head() {
    const { t } = useTranslation();
    const { pathname } = useLocation();

    // Canonical: immer ohne Query-Parameter, www-los
    const canonical = `${CANONICAL_BASE}${pathname === '/' ? '/' : pathname}`;

    // NotFound-Seiten nicht indexieren
    const knownRoutes = ['/', '/imprint', '/privacy'];
    const isNotFound = !knownRoutes.includes(pathname);

    return (
        <Helmet>
            <meta name="description" content={t('metaDescription')} />
            <title>{t('pageTitle')}</title>
            <link rel="icon" type="image/png" sizes="32x32" href="/favicon.png" />
            <link rel="canonical" href={canonical} />
            {isNotFound && <meta name="robots" content="noindex, nofollow" />}
            <meta http-equiv="Permissions-Policy" content="accelerometer=*, magnetometer=*, gyroscope=*, picture-in-picture=*"></meta>
        </Helmet>
    );
}


function App() {
    const { t } = useTranslation();

    {/* Circles do not appear on mobile devices */}
    const [isMobile, setIsMobile] = useState(window.innerWidth < settings.geometry.breakpoints.md);
    
    {/* Scroll to the top arrow only on large desktops */}
    const [isLargeDesktop, setIsLargeDesktop] = useState(window.innerWidth > settings.geometry.breakpoints.md);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < settings.geometry.breakpoints.md);
            setIsLargeDesktop(window.innerWidth > settings.geometry.breakpoints.xxxl);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <BrowserRouter>
            <Suspense fallback={<div>{t('loading')}</div>}>
                <ScrollToAnchor />
                <div className="canvas">
                    <Head />
                    <main>
                        <div className="spacer-s" />
                        {isMobile ? <NavBarMobile /> : <NavBarDesktop />}
                        <div className="spacer-s" />
                        <Routes>
                            <Route index element={
                                <Suspense fallback={<div>{t('loading')}</div>}>
                                    <Why />
                                    <div className="spacer-2xl" />
                                    <Statement />
                                    <div className="spacer-2xl" />
                                    <Problem />
                                    <div className="spacer-2xl" />
                                    <Mission />
                                    <References />
                                    <div className="spacer-2xl" />
                                    <How />
                                    <div className="spacer-2xl" />
                                    <Library />
                                    <div className="spacer-2xl" />
                                    <Who />
                                    <div className="spacer-2xl" />
                                    <Tools />
                                    <div className="spacer-2xl" />
                                    <WriteUs />
                                    <Footer />
                                </Suspense>
                            } />
                            <Route path="/imprint" element={<Imprint />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                        {isLargeDesktop ? <ScrollToTopArrow /> : ''}
                    </main>
                </div>
            </Suspense>
        </BrowserRouter>
    );
}

export default App;